import { useState, useEffect } from 'react';
import { Check, ChevronDown, Tag, Search } from 'lucide-react';
import FilterService from '../../services/filterService';

function CategoryFilter({ 
  selectedCategories = [], 
  selectedDepartments = [], 
  selectedExpenseGroups = [], 
  selectedExpenseTypes = [], 
  onChange 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Get available categories based on selected filters
    setTimeout(() => {
      const availableCategories = FilterService.getAvailableCategories(
        selectedDepartments, 
        selectedExpenseGroups, 
        selectedExpenseTypes
      );
      setCategories(availableCategories);
      setLoading(false);
    }, 300);
  }, [selectedDepartments, selectedExpenseGroups, selectedExpenseTypes]);

  const handleCategoryToggle = (categoryId) => {
    const newSelection = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    onChange(newSelection);
  };

  const selectAll = () => {
    onChange(filteredCategories.map(cat => cat.id));
  };

  const clearAll = () => {
    onChange([]);
  };

  const getSelectedCategoryNames = () => {
    return categories
      .filter(cat => selectedCategories.includes(cat.id))
      .map(cat => cat.name);
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.group.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group categories by group
  const groupedCategories = filteredCategories.reduce((groups, category) => {
    const group = category.group;
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(category);
    return groups;
  }, {});

  const selectedNames = getSelectedCategoryNames();
  const hasSelection = selectedCategories.length > 0;

  // Show disabled state if no departments selected
  const isDisabled = selectedDepartments.length === 0;

  if (loading) {
    return (
      <div 
        className="p-3 rounded-lg border animate-pulse"
        style={{ 
          background: 'var(--bg)',
          borderColor: 'var(--border)'
        }}
      >
        <div 
          className="h-4 rounded"
          style={{ background: 'var(--border)' }}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Category Selection Display */}
      <div 
        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${
          isDisabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        style={{ 
          background: hasSelection ? 'rgba(139, 69, 19, 0.05)' : 'var(--bg)',
          borderColor: hasSelection ? 'var(--primary)' : 'var(--border)'
        }}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Tag className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary)' }} />
          <span className="text-sm truncate" style={{ color: 'var(--text)' }}>
            {isDisabled 
              ? 'Select departments first'
              : hasSelection 
                ? selectedNames.length === 1 
                  ? selectedNames[0]
                  : `${selectedNames.length} categories selected`
                : 'Select categories'
            }
          </span>
        </div>
        
        <ChevronDown 
          className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--muted)' }}
        />
      </div>

      {/* Dropdown */}
      {isOpen && !isDisabled && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 p-2 rounded-lg border shadow-lg z-10 max-h-80 overflow-y-auto"
          style={{ 
            background: 'var(--panel)',
            borderColor: 'var(--border)'
          }}
        >
          {/* Header with Search */}
          <div className="p-2 border-b mb-2" style={{ borderColor: 'var(--border)' }}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>
                Select Categories
              </span>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-xs px-2 py-1 rounded"
                  style={{ 
                    background: 'var(--primary)',
                    color: 'white'
                  }}
                >
                  All
                </button>
                <button
                  onClick={clearAll}
                  className="text-xs px-2 py-1 rounded border"
                  style={{ 
                    borderColor: 'var(--border)',
                    color: 'var(--text)'
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
            
            {/* Search Input */}
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--muted)' }} />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-7 pr-2 py-1 text-xs rounded border"
                style={{ 
                  background: 'var(--bg)',
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              />
            </div>
          </div>

          {/* Category Groups */}
          <div className="space-y-3">
            {Object.entries(groupedCategories).map(([groupName, groupCategories]) => (
              <div key={groupName}>
                {/* Group Header */}
                <div className="text-xs font-medium mb-1 px-2" style={{ color: 'var(--muted)' }}>
                  {groupName}
                </div>
                
                {/* Group Categories */}
                <div className="space-y-1">
                  {groupCategories.map((category) => {
                    const isSelected = selectedCategories.includes(category.id);
                    
                    return (
                      <div
                        key={category.id}
                        className="flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-opacity-50"
                        style={{ 
                          background: isSelected ? 'rgba(139, 69, 19, 0.1)' : 'transparent'
                        }}
                        onClick={() => handleCategoryToggle(category.id)}
                      >
                        {/* Checkbox */}
                        <div 
                          className="w-4 h-4 rounded border-2 flex items-center justify-center"
                          style={{ 
                            borderColor: isSelected ? category.color : 'var(--border)',
                            background: isSelected ? category.color : 'transparent'
                          }}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>

                        {/* Category Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ background: category.color }}
                            />
                            <span className="text-sm" style={{ color: 'var(--text)' }}>
                              {category.name}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* No Results */}
          {filteredCategories.length === 0 && (
            <div className="text-center py-4">
              <Tag className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--muted)' }} />
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                No categories found for "{searchTerm}"
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-2 pt-2 border-t text-center" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => setIsOpen(false)}
              className="text-xs px-3 py-1 rounded"
              style={{ 
                background: 'var(--border)',
                color: 'var(--text)'
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryFilter;